import { Button } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { ButtonVariant } from "@amzn/stencil-react-components/dist/submodules/button/interfaces";
import { WithFlyout } from "@amzn/stencil-react-components/flyout";
import { Col, Container, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { TabBar, TabPanel, TabSwitcher, useTabs } from "@amzn/stencil-react-components/tabs";
import { Text } from "@amzn/stencil-react-components/text";
import { ToggleButton, ToggleButtonGroup } from "@amzn/stencil-react-components/toggle-button";
import { IconExternalLink } from "@amzn/stencil-react-icons";
import Link from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import {
  CFMModuleName,
  CustomerFeedback,
  CustomerFeedbackAlignment,
  CustomerFeedbackIconPosition,
  CustomerFeedbackMargins,
} from "common/components";
import { HardwareSupportFlyout } from "common/components/HardwareSupportFlyout";
import { useHardwareFlyout } from "common/components/HardwareSupportFlyout/useHardwareFlyout";
import { MySoftwareAssetsTable } from "common/components/MyAssetsTable/MySoftwareAssetsTable";
import { MySoftwareOrdersTable } from "common/components/MyOrdersTable";
import { MyHardwareOrdersTable } from "common/components/MyOrdersTable/MyHardwareOrdersTable";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS, USE_MY_ASSETS } from "config";
import { UserPermissions } from "lib/services";
import React, { FC, useEffect, useState } from "react";

const tabs = ["My Assets", "Orders"];
const CFM_STYLE = "cfm-scroll";

const URL_PARAMS = {
  ORDERS_TAB: "tab",
};

export const TABLE_INDEX = {
  SOFTWARE: 0,
  HARDWARE: 1,
};

export const OrdersPage: FC = () => {
  const hash = window.location.hash.replace("#", "");
  const params = new URLSearchParams(window.location.search);
  const tab = params.get(URL_PARAMS.ORDERS_TAB);
  const [selectedTable, setSelectedTable] = useState<number>(tab && Number(tab) ? Number(tab) : 0);
  const useHardwareShop = UserPermissions.current.hasPermission(PERMISSIONS.USE_HARDWARE_SHOP);
  const { tabBarProps, tabSwitcherProps } = useTabs({ tabs, defaultTab: "Orders" });

  const { openFlyoutRef } = useHardwareFlyout({ setSelectedTable, selectedTable });

  const handleSelection = (event: React.FormEvent<HTMLElement>, value: string | string[] | undefined) => {
    if (value === "software") {
      trackUxmCustomEvent("orders-tab", { tab: "software" });
      setSelectedTable(TABLE_INDEX.SOFTWARE);
    } else if (value === "hardware") {
      trackUxmCustomEvent("orders-tab", { tab: "hardware" });
      setSelectedTable(TABLE_INDEX.HARDWARE);
    }
  };

  // Update URL when state changes
  useEffect(() => {
    if (selectedTable === TABLE_INDEX.HARDWARE) {
      params.set(URL_PARAMS.ORDERS_TAB, TABLE_INDEX.HARDWARE.toString());
    } else {
      params.set(URL_PARAMS.ORDERS_TAB, TABLE_INDEX.SOFTWARE.toString());
    }

    // Update the URL without causing a page reload
    window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}${hash ? "#" : ""}${hash}`);
  }, [selectedTable]);

  const renderCFM = () => {
    const module =
      selectedTable == TABLE_INDEX.SOFTWARE ? CFMModuleName.myOrdersSoftware : CFMModuleName.myOrdersHardware;
    return (
      <Card
        className={`cfm-general ${CFM_STYLE}`}
        data-testid="cfm-card"
        isElevated={true}
        border={"1px solid neutral50"}
        style={{ position: "fixed", top: "55%", height: "auto" }}
      >
        <CustomerFeedback
          moduleName={module}
          alignment={CustomerFeedbackAlignment.Centered}
          position={CustomerFeedbackIconPosition.Bottom}
          margin={CustomerFeedbackMargins.None}
        />
      </Card>
    );
  };

  return (
    <PageMainContainer>
      <Container isFullWidth={false}>
        <Col gridGap="S200" width={"100%"}>
          <Row padding="S300" gridGap="S400" width={"100%"}>
            {USE_MY_ASSETS ? (
              <Row gridGap="S400" width={"100%"} justifyContent="space-between" alignItems="center">
                <TabBar {...tabBarProps} />
              </Row>
            ) : (
              <Text fontSize="T400" color="neutral90">
                {getString("myOrders.topBar")}
              </Text>
            )}
          </Row>

          <Row gridGap="S400" width={"100%"} justifyContent="space-between" alignItems="center">
            {useHardwareShop && (
              <ToggleButtonGroup onChange={handleSelection} data-test-id="toggle-group">
                <ToggleButton isSelected={selectedTable === TABLE_INDEX.SOFTWARE} value="software">
                  {getString("myOrders.softwareToggle")}
                </ToggleButton>
                <ToggleButton isSelected={selectedTable === TABLE_INDEX.HARDWARE} value="hardware">
                  {getString("myOrders.hardwareToggle")}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <Spacer />
            {selectedTable === TABLE_INDEX.SOFTWARE ? (
              <Link
                href="https://w.amazon.com/bin/view/ITMarketplaceSoftwareOrdering/ITMP_order_support/"
                target="_blank"
                touchpoint="order-support-software"
                icon={<IconExternalLink title="Opens in a new tab" />}
              >
                {getString("myOrders.softwareSupport")}
              </Link>
            ) : (
              <WithFlyout renderFlyout={({ close }) => <HardwareSupportFlyout onClose={close} />}>
                {({ open }) => {
                  openFlyoutRef.current = open;
                  return (
                    <Button
                      dataTestId="hardware-support-button"
                      variant={ButtonVariant.Tertiary}
                      onClick={() => {
                        trackUxmCustomEvent("order-support-hardware", {});
                        open();
                      }}
                    >
                      {getString("myOrders.hardwareSupport")}
                    </Button>
                  );
                }}
              </WithFlyout>
            )}
          </Row>
          <Row padding="S300" gridGap="S400" width={"100%"}>
            <Col gridGap="S200" width={"100%"}>
              <TabSwitcher {...tabSwitcherProps}>
                <TabPanel value="My Assets">
                  {selectedTable === TABLE_INDEX.SOFTWARE ? (
                    <MySoftwareAssetsTable />
                  ) : (
                    <Text>Hardware assets not supported yet</Text>
                  )}
                </TabPanel>
                <TabPanel value="Orders">
                  {selectedTable === TABLE_INDEX.SOFTWARE ? <MySoftwareOrdersTable /> : <MyHardwareOrdersTable />}
                </TabPanel>
              </TabSwitcher>
            </Col>
          </Row>
        </Col>
        {renderCFM()}
      </Container>
    </PageMainContainer>
  );
};
